/** @jsxImportSource @emotion/react */

import React from 'react';
import styled from 'styled-components';
import * as color from '@constant/color';
import * as size from '@constant/size';
import TextBlock from '@component/common/block/text-block';
import IconTextBlock from '@component/common/block/icon-text-block';
import Gap from '@component/common/gap';
import Canvas from '@design-system/canvas';
import Chip from '@design-system/chip';
import Quote from '@design-system/quote';
import StudyNote from '@component/iyagigo/common/study-note';
import { ETheme } from '@datatype/type';
import { EApiLanguageCode, IAiVocabulary, IClozeExplanation } from '@datatype/iyagigo/api-type';
import { addFurigana, completeSentence, createFuriganaMap, markBlanks, replaceDashWithTilde } from '@util/string';
// 기록용: 영어 발음 표기 복구 시 replaceDashForPronunciation도 import 필요
// import { replaceDashForPronunciation } from '@util/string';
import { v4 as uuid4 } from 'uuid';
import { useTypedSelector } from '@hook/common/useStore';
import { IConversationProblemToastData, IKeyExpressionExplanationToastData, TNotificationToast, isClozeExplanationToastDataType, isVocabularyExplanationToastDataType, isConversationProblemToastDataType, isKeyExpressionExplanationToastDataType } from '@datatype/iyagigo/type';

export type TClozeExplanationSection = 'answer' | 'usage' | 'example' | 'kanji' | 'inflection' | 'alternative' | 'add';

const ALL_SECTIONS: TClozeExplanationSection[] = [
  'answer',
  'usage',
  'example',
  'kanji',
  'inflection',
  'alternative',
  'add',
];

interface IProps {
  answerExplanation: IClozeExplanation | null | undefined;
  addExplanation?: IClozeExplanation[] | null;
  clozeType?: 'vocabulary' | 'grammar';
  labelSize?: 'medium' | 'large';
  sections?: TClozeExplanationSection[];
}

// 기록용: 영어 발음 표기 복구 시 사용
// const PRONUNCIATION_COLOR = color.GRAY_1;

const ContentContainer = styled.div`
  display: flex;
  flex-flow: column nowrap;
  gap: ${size.MARGIN_1};
  width: 100%;
`;

const ExplanationItemContainer = styled.div`
  display: flex;
  flex-flow: column nowrap;
  justify-content: flex-start;
  align-items: flex-start;
`;

const MeaningContainer = styled.div`
  display: flex;
  flex-flow: row wrap;
  justify-content: flex-start;
  align-items: center;
  gap: ${size.MARGIN_1};
`;

const KanjiYomiContainer = styled.div`
  display: flex;
  flex-flow: row nowrap;
  justify-content: flex-start;
  align-items: center;
  gap: 5px;
`;

const DescriptionContainer = styled.div`
  display: flex;
  flex-flow: column nowrap;
  align-items: flex-start;
  gap: 5px;
`;

// keyword를 정규화(대시→물결)하고, 일본어 학습 중이면 발음으로 후리가나를 붙인다.
const normalizeKeyword = (keyword: string, pronunciation: string | undefined, isJapanese: boolean): string => {
  const normalizedKeyword = replaceDashWithTilde(keyword);
  if (isJapanese && pronunciation) {
    return addFurigana(normalizedKeyword, createFuriganaMap(normalizedKeyword, pronunciation), 'start');
  }
  return normalizedKeyword;
  // 기록용: 기존에는 영어일 때 keyword 옆에 발음 괄호 표기를 보여줬음. 현재는 영어 발음 미표시.
  // return `${normalizedKeyword}<span style="color: ${PRONUNCIATION_COLOR}; font-weight: normal; font-size: small">(${replaceDashForPronunciation(pronunciation)})</span>`;
};

// "<keyword>의 뜻은 <meaning>입니다." HTML 문자열 생성 (TextBlock에 dangerouslySetInnerHTML로 렌더됨)
const buildKeywordMeaning = (
  keyword: string,
  pronunciation: string | undefined,
  meaning: string[] | undefined,
  isJapanese: boolean,
): string =>
  `<span style="color: ${color.BLACK_2}; font-weight: bold;">${normalizeKeyword(keyword, pronunciation, isJapanese)}</span>의 뜻은 <span style="color: ${color.BLACK_2}; font-weight: bold;">${meaning?.map((m) => replaceDashWithTilde(m)).join(', ')}</span>입니다.`;

// 한자(character/meaning/onyomi/kunyomi) 카드 목록 렌더. cloze/vocabulary 공통.
const renderKanji = (kanji: IClozeExplanation['kanji']) =>
  (kanji ?? []).map((item) => (
    <ExplanationItemContainer key={uuid4()}>
      <MeaningContainer>
        <TextBlock
          text={item.character}
          design={{ text: { size: 'medium', color: color.BLACK_2, bold: true }, box: { alignX: 'left' } }}
        />
        <TextBlock
          text={item.meaning?.[0]}
          design={{ text: { size: 'medium', color: color.BLACK_2, bold: false }, box: { alignX: 'left' } }}
        />
      </MeaningContainer>
      <DescriptionContainer>
        {item.kunyomi?.length > 0 && (
          <KanjiYomiContainer>
            <TextBlock
              text={'· 훈독'}
              design={{ text: { size: 'medium', color: color.GRAY_1, bold: false }, box: { alignX: 'left' } }}
            />
            <TextBlock
              text={item.kunyomi.join(', ')}
              design={{ text: { size: 'medium', color: color.GRAY_1, bold: false }, box: { alignX: 'left' } }}
            />
          </KanjiYomiContainer>
        )}
        {item.onyomi?.length > 0 && (
          <KanjiYomiContainer>
            <TextBlock
              text={'· 음독'}
              design={{ text: { size: 'medium', color: color.GRAY_1, bold: false }, box: { alignX: 'left' } }}
            />
            <TextBlock
              text={item.onyomi.join(', ')}
              design={{ text: { size: 'medium', color: color.GRAY_1, bold: false }, box: { alignX: 'left' } }}
            />
          </KanjiYomiContainer>
        )}
      </DescriptionContainer>
    </ExplanationItemContainer>
  ));

// usage(쓰임새 설명) 한 줄. usage가 없으면 아무것도 그리지 않는다.
const Usage: React.FC<{ usage: string | null | undefined }> = ({ usage }) =>
  usage ? (
    <TextBlock
      text={replaceDashWithTilde(usage)}
      design={{
        text: { size: 'medium', color: color.BLACK_4, bold: false, underline: false },
        box: { alignX: 'left' },
      }}
    />
  ) : null;

// 예문/번역(해석). '예문' 칩과 Quote로 묶어 표시한다.
// 예문(sentence)은 본문색(BLACK_2), 번역(translation)은 보조색(GRAY_1)으로 고정.
// 단일 wrapper로 감싸 부모 컨테이너의 gap 영향을 받지 않고 내부 간격을 자체 제어한다.
const ExampleContainer = styled.div`
  display: flex;
  flex-flow: column nowrap;
  align-items: flex-start;
  gap: 5px;
`;

export const Example: React.FC<{
  sentence: string | undefined;
  translation: string | undefined;
}> = ({ sentence, translation }) =>
  sentence && translation ? (
    <ExampleContainer>
      <Chip text='예문' size='tiny' usage='static' kind='outlined' shape='round' width={'35px'} theme={ETheme.BROWN} />
      <Quote size='small'>
        <TextBlock
          text={sentence}
          design={{ text: { size: 'small', color: color.BLACK_2 }, box: { alignX: 'left' } }}
        />
        <TextBlock
          text={translation}
          design={{ text: { size: 'tiny', color: color.GRAY_1 }, box: { alignX: 'left' } }}
        />
      </Quote>
    </ExampleContainer>
  ) : null;

// cloze/vocabulary 해설 본문(키워드 뜻/usage/예문/한자/형변화/유사어휘/더 알아보기).
// study·conversation-study 화면이 IClozeExplanation을 직접 넘겨 재사용한다.
const ClozeExplanation: React.FC<IProps> = ({
  answerExplanation,
  addExplanation,
  clozeType = 'vocabulary',
  labelSize = 'large',
  sections = ALL_SECTIONS,
}) => {
  const studyLanguage = useTypedSelector((state) => state.rootReducer?.studentReducer?.currentStudyLanguage) ?? null;
  const isJapanese = studyLanguage === EApiLanguageCode.JAPANESE;

  if (!answerExplanation) return null;

  const getIsShow = (section: TClozeExplanationSection) => sections.includes(section);

  const labelDesign = {
    text: { size: labelSize, color: color.BLACK_2, bold: true },
    box: { alignX: 'left', width: '100%' },
  } as const;

  const keywordMeaning = buildKeywordMeaning(
    answerExplanation.keyword,
    answerExplanation.pronunciation?.[0],
    answerExplanation.meaning,
    isJapanese,
  );

  const example = answerExplanation.example?.[0];
  const kanji = answerExplanation.kanji ?? [];
  const inflection = answerExplanation.inflection ?? [];
  const alternative = answerExplanation.alternative ?? [];

  const Inflection = inflection.map((item) => {
    let label = '';
    if (isJapanese) {
      if (item.type === 'base') label = '기본형';
      else if (item.type === 'past') label = '과거형';
      else if (item.type === 'negative') label = '부정형';
      else if (item.type === 'polite') label = '정중형';
    } else {
      if (item.type === 'singular') label = '단수형';
      else if (item.type === 'plural') label = '복수형';
      else if (item.type === 'base') label = '기본형';
      else if (item.type === 'comparative') label = '비교급';
      else if (item.type === 'superlative') label = '최상급';
      else if (item.type === 'thirdPersonSingular') label = '3인칭 단수';
      else if (item.type === 'past') label = '과거형';
      else if (item.type === 'pastParticiple') label = '과거분사';
    }
    return (
      <MeaningContainer key={uuid4()}>
        <TextBlock
          text={label}
          design={{ text: { size: 'medium', color: color.BLACK_2, bold: true }, box: { alignX: 'left' } }}
        />
        <TextBlock
          text={item.form}
          design={{ text: { size: 'medium', color: color.BLACK_2, bold: false }, box: { alignX: 'left' } }}
        />
      </MeaningContainer>
    );
  });

  const Kanji = renderKanji(kanji);

  const Alternative = alternative.map((item) => (
    <ExplanationItemContainer key={uuid4()}>
      <MeaningContainer>
        <TextBlock
          text={normalizeKeyword(item.keyword, item.pronunciation?.[0], isJapanese)}
          design={{ text: { size: 'medium', color: color.BLACK_2, bold: true }, box: { alignX: 'left' } }}
        />
        <TextBlock
          text={replaceDashWithTilde(item.meaning?.[0])}
          design={{ text: { size: 'medium', color: color.BLACK_2, bold: false }, box: { alignX: 'left' } }}
        />
      </MeaningContainer>
      {item.usage && <Usage usage={item.usage} />}
      {item?.example?.[0]?.sentence && item?.example?.[0]?.translation && (
        <>
          <Gap height={size.MARGIN_1} />
          <Example sentence={item.example[0].sentence} translation={item.example[0].translation} />
        </>
      )}
    </ExplanationItemContainer>
  ));

  const AddExplanation = (addExplanation ?? []).map((item) => (
    <MeaningContainer key={uuid4()}>
      <TextBlock
        text={normalizeKeyword(item.keyword, item.pronunciation?.[0], isJapanese)}
        design={{ text: { size: 'medium', color: color.BLACK_2, bold: true }, box: { alignX: 'left' } }}
      />
      <TextBlock
        text={replaceDashWithTilde(item.meaning?.[0])}
        design={{ text: { size: 'medium', color: color.BLACK_2, bold: false }, box: { alignX: 'left' } }}
      />
    </MeaningContainer>
  ));

  return (
    <>
      {getIsShow('answer') && (
        <TextBlock
          text={keywordMeaning}
          design={{ text: { size: 'large', color: color.GRAY_1, bold: false }, box: { alignX: 'left' } }}
        />
      )}
      {getIsShow('usage') && answerExplanation.usage && (
        <>
          <Gap height={size.MARGIN_1} />
          <Usage usage={answerExplanation.usage} />
        </>
      )}
      {getIsShow('example') && example?.sentence && example?.translation && (
        <>
          <Gap height={size.MARGIN_1} />
          <Example sentence={example.sentence} translation={example.translation} />
        </>
      )}
      {getIsShow('kanji') && kanji.length > 0 && (
        <>
          <Gap height={size.MARGIN_2} />
          <TextBlock text={'한자'} design={labelDesign} />
          <Canvas theme={ETheme.GRAY} shape='round' width='100%'>
            <ContentContainer>{Kanji}</ContentContainer>
          </Canvas>
        </>
      )}
      {getIsShow('inflection') && inflection.length > 0 && (
        <>
          <Gap height={size.MARGIN_2} />
          <TextBlock text={'형변화'} design={labelDesign} />
          <Canvas theme={ETheme.GRAY} shape='round' width='100%'>
            <ContentContainer>{Inflection}</ContentContainer>
          </Canvas>
        </>
      )}
      {getIsShow('alternative') && alternative.length > 0 && (
        <>
          <Gap height={size.MARGIN_2} />
          <TextBlock text={clozeType === 'vocabulary' ? '유사한 어휘' : '유사한 문법'} design={labelDesign} />
          <Canvas theme={ETheme.GRAY} shape='round' width='100%'>
            <ContentContainer>{Alternative}</ContentContainer>
          </Canvas>
        </>
      )}
      {getIsShow('add') && addExplanation && addExplanation.length > 0 && (
        <>
          <Gap height={size.MARGIN_2} />
          <TextBlock text={'더 알아보기'} design={labelDesign} />
          <Canvas theme={ETheme.GRAY} shape='round' width='100%'>
            <ContentContainer>{AddExplanation}</ContentContainer>
          </Canvas>
        </>
      )}
    </>
  );
};

// ############### 라벨(섹션 제목) 공통 디자인 ###############
// 'large bold left' 라벨. key-expression 카드 제목 등에서 사용.
const LABEL_TEXT_DESIGN = {
  text: { size: 'large', color: color.BLACK_2, bold: true },
  box: { alignX: 'left', width: '100%' },
} as const;

// ############### conversation-problem 본문 ###############
const ChoicesContainer = styled.div`
  display: flex;
  flex-flow: column nowrap;
  gap: ${size.MARGIN_1};
`;

const ChoiceContainer = styled.div<{ $isCorrect: boolean }>`
  padding: ${size.MARGIN_1} ${size.MARGIN_1} ${size.MARGIN_1} ${size.MARGIN_1};
  border-radius: ${size.BORDER_RADIUS_1};
  border: ${size.BORDER_WIDTH_1} solid ${({ $isCorrect }) => ($isCorrect ? color.GREEN_3 : color.RED_2)};
  display: flex;
  flex-flow: column nowrap;
  gap: 5px;
`;

const ChoiceLabelContainer = styled.div`
  display: flex;
  flex-flow: row nowrap;
  align-items: center;
  justify-content: space-between;
  width: 100%;
`;

const createResultBannerDesign = (isCorrect: boolean) => {
  const resultColor = isCorrect ? color.GREEN_3 : color.RED_2;
  return {
    text: { size: 'large', color: resultColor, bold: true },
    icon: { size: 'medium', color: resultColor, kind: 'outlined' },
    box: { alignX: 'center', width: '100%', gap: '5px' },
  } as const;
};

const CHOICE_LABEL_DESIGN = {
  text: { size: 'medium', color: color.GRAY_1, bold: true },
  box: { alignX: 'left' },
} as const;

const CHOICE_SENTENCE_DESIGN = {
  text: { size: 'medium', color: color.BLACK_2, bold: true },
  box: { alignX: 'left' },
} as const;

const CHOICE_TRANSLATION_DESIGN = {
  text: { size: 'medium', color: color.GRAY_1, bold: false },
  box: { alignX: 'left' },
} as const;

const CHOICE_EXPLANATION_DESIGN = {
  text: { size: 'small', color: color.BLACK_2, bold: false },
  box: {
    alignX: 'left',
    backgroundColor: color.GRAY_5,
    padding: `${size.MARGIN_1} ${size.MARGIN_1} ${size.MARGIN_1} ${size.MARGIN_1}`,
    borderRadius: `${size.BORDER_RADIUS_1}`,
  },
} as const;

const PROBLEM_EXPLANATION_DESIGN = {
  text: { size: 'medium', color: color.BLACK_2, bold: false },
  box: {
    alignX: 'left',
    backgroundColor: color.BROWN_4,
    padding: `${size.MARGIN_1} ${size.MARGIN_1} ${size.MARGIN_1} ${size.MARGIN_1}`,
    borderRadius: `${size.BORDER_RADIUS_1}`,
  },
} as const;

const CORRECT_ANSWER_DESIGN = {
  text: { size: 'medium', color: color.BROWN_1, bold: true },
  box: { alignX: 'left' },
} as const;

// 대화 문제(정답/오답) 해설 본문. 정답 배너 + 문제 해설 + 보기 카드 목록.
const ConversationExplanation: React.FC<{ data: IConversationProblemToastData['data'] }> = ({ data }) => {
  const studyLanguage = useTypedSelector((state) => state.rootReducer?.studentReducer?.currentStudyLanguage) ?? null;
  const isJapanese = studyLanguage === EApiLanguageCode.JAPANESE;
  const { isCorrect, choices, problemExplanation } = data;

  const correctLabels = choices
    .map((choice, idx) => (choice.isCorrect ? `보기 ${String.fromCharCode(65 + idx)}` : null))
    .filter((label): label is string => label !== null);
  const correctAnswerText = correctLabels.length > 0 ? `따라서 ${correctLabels.join(', ')}가 정답입니다.` : '';

  // 라벨(A/B/C...)은 원래 인덱스 기준으로 고정. 사용자가 고른 보기가 첫 번째 행에 오도록 표시 순서만 정렬
  const labelChoices = choices.map((choice, idx) => ({
    choice,
    label: String.fromCharCode(65 + idx),
  }));
  const sortedChoices = [...labelChoices].sort((a, b) => {
    if (a.choice.isSelected === b.choice.isSelected) return 0;
    return a.choice.isSelected ? -1 : 1;
  });

  return (
    <>
      <IconTextBlock
        icon={isCorrect ? 'circle' : 'close'}
        text={isCorrect ? '정답이에요' : '오답이에요'}
        design={createResultBannerDesign(isCorrect)}
      />
      <Gap height={size.MARGIN_1} />
      {problemExplanation && <TextBlock text={problemExplanation} design={PROBLEM_EXPLANATION_DESIGN} />}
      <Gap height={size.MARGIN_1} />
      <TextBlock text={correctAnswerText} design={CORRECT_ANSWER_DESIGN} />
      <Gap height={size.MARGIN_1} />
      <ChoicesContainer>
        {sortedChoices.map(({ choice, label }) => {
          const isWrongSelected = choice.isSelected && !choice.isCorrect;
          return (
            <ChoiceContainer key={uuid4()} $isCorrect={choice.isCorrect}>
              <ChoiceLabelContainer>
                <TextBlock text={`보기 ${label}`} design={CHOICE_LABEL_DESIGN} />
                {choice.isCorrect && (
                  <TextBlock
                    text={'정답'}
                    design={{ text: { size: 'small', color: color.GREEN_3, bold: true }, box: { alignX: 'left' } }}
                  />
                )}
                {isWrongSelected && (
                  <TextBlock
                    text={'고른 보기'}
                    design={{ text: { size: 'small', color: color.RED_2, bold: true }, box: { alignX: 'left' } }}
                  />
                )}
              </ChoiceLabelContainer>
              {choice.sentence && (
                <TextBlock
                  text={choice.sentence}
                  annotation={isJapanese && choice.pronunciation ? { reading: choice.pronunciation } : undefined}
                  design={CHOICE_SENTENCE_DESIGN}
                />
              )}
              {choice.translation && (
                <TextBlock text={replaceDashWithTilde(choice.translation)} design={CHOICE_TRANSLATION_DESIGN} />
              )}
              {choice.explanation && (
                <TextBlock text={replaceDashWithTilde(choice.explanation)} design={CHOICE_EXPLANATION_DESIGN} />
              )}
            </ChoiceContainer>
          );
        })}
      </ChoicesContainer>
    </>
  );
};

// ############### key-expression 본문 ###############
const KeyExpressionContainer = styled.div`
  display: flex;
  flex-flow: column nowrap;
  background-color: ${color.BROWN_4};
  padding: ${size.MARGIN_1};
  border-radius: ${size.BORDER_RADIUS_1};
`;

// 핵심 표현(오늘의 표현) 해설 본문. key/plain 공통 카드 + plain 모드 학습 노트.
const KeyExpressionExplanation: React.FC<{ data: IKeyExpressionExplanationToastData['data'] }> = ({ data }) => {
  const { mode, sourceKeySegment, keySegment, keyExpressionNote, expressionNote } = data;

  const markedKeyExpression = markBlanks(
    completeSentence(sourceKeySegment?.keyExpressionTemplate ?? '', sourceKeySegment?.keyExpressionAnswer ?? [], true),
  );

  return (
    <>
      {/* ############### 오늘의 핵심 표현 카드 (key/plain 공통) ############### */}
      {(mode === 'key' || mode === 'plain') && (
        <>
          <TextBlock text='오늘의 표현' design={LABEL_TEXT_DESIGN} />
          <Gap height={'5px'} />
          <KeyExpressionContainer>
            <TextBlock
              text={markedKeyExpression || keySegment.keyExpression}
              annotation={
                sourceKeySegment?.keyExpressionPronunciation
                  ? { reading: sourceKeySegment.keyExpressionPronunciation }
                  : undefined
              }
              blank={{ textColor: color.GREEN_4 }}
              design={{
                text: { size: 'large', color: color.BLACK_2, bold: true },
                box: { alignX: 'left' },
              }}
            />
            {keySegment.keyExpressionTranslation && (
              <TextBlock
                text={keySegment.keyExpressionTranslation}
                design={{
                  text: { size: 'large', color: color.BLACK_2, underline: true, bold: false },
                  box: { alignX: 'left' },
                }}
              />
            )}
            {keySegment.keyExpressionContext && (
              <>
                <Gap height={'2px'} />
                <TextBlock
                  text={keySegment.keyExpressionContext}
                  design={{
                    text: { size: 'medium', color: color.GRAY_1, bold: false },
                    box: { alignX: 'left' },
                  }}
                />
              </>
            )}
            {keySegment.keyExpressionExample && keySegment.keyExpressionExample.length > 0 && (
              <>
                <Gap height={size.MARGIN_1} />
                {keySegment.keyExpressionExample.map((example) => (
                  <Example key={uuid4()} sentence={example.sentence} translation={example.translation} />
                ))}
              </>
            )}
          </KeyExpressionContainer>
        </>
      )}

      {/* ############### plain 모드: 어휘/문법 노트 추가 ############### */}
      {mode === 'plain' && (
        <>
          <Gap height={size.MARGIN_2} />
          <TextBlock text='학습 노트' design={LABEL_TEXT_DESIGN} />
          <Gap height={'5px'} />
          <StudyNote
            vocabularies={[...(keyExpressionNote?.vocabulary ?? []), ...(expressionNote?.vocabulary ?? [])]}
            grammars={[...(keyExpressionNote?.grammar ?? []), ...(expressionNote?.grammar ?? [])]}
          />
        </>
      )}
    </>
  );
};

// ############### 단어집(AI 어휘) 해설 본문 ###############
// 단어집 해설은 한국 사용자 대상이므로 한국어(Ko) 필드만 사용한다.
// IAiVocabulary를 직접 받아 answer/usage/example/kanji 4섹션을 렌더 (cloze 해설과 동일 UI).
const AiVocabularyExplanation: React.FC<{ aiVocabulary: IAiVocabulary }> = ({ aiVocabulary }) => {
  const studyLanguage = useTypedSelector((state) => state.rootReducer?.studentReducer?.currentStudyLanguage) ?? null;
  const isJapanese = studyLanguage === EApiLanguageCode.JAPANESE;

  const keywordMeaning = buildKeywordMeaning(
    aiVocabulary.source,
    aiVocabulary.sourcePronunciation?.[0],
    aiVocabulary.meaningKo,
    isJapanese,
  );
  const example = aiVocabulary.exampleKo?.[0];
  const kanji = aiVocabulary.kanjiKo ?? [];

  return (
    <>
      {/* answer */}
      <TextBlock
        text={keywordMeaning}
        design={{ text: { size: 'large', color: color.GRAY_1, bold: false }, box: { alignX: 'left' } }}
      />
      {/* usage */}
      {aiVocabulary.usageKo && (
        <>
          <Gap height={size.MARGIN_1} />
          <Usage usage={aiVocabulary.usageKo} />
        </>
      )}
      {/* example */}
      {example?.sentence && example?.translation && (
        <>
          <Gap height={size.MARGIN_1} />
          <Example sentence={example.sentence} translation={example.translation} />
        </>
      )}
      {/* kanji */}
      {kanji.length > 0 && (
        <>
          <Gap height={size.MARGIN_2} />
          <TextBlock
            text={'한자'}
            design={{
              text: { size: 'large', color: color.BLACK_2, bold: true },
              box: { alignX: 'left', width: '100%' },
            }}
          />
          <Canvas theme={ETheme.GRAY} shape='round' width='100%'>
            <ContentContainer>{renderKanji(kanji)}</ContentContainer>
          </Canvas>
        </>
      )}
    </>
  );
};

// ############### 통합 진입점 (유일한 export) ###############
// 두 가지 호출 방식을 모두 지원한다.
//  (1) toast: data(TNotificationToast)를 받아 type에 따라 알맞은 해설 본문으로 분기
//      (cloze/vocabulary → cloze 본문, conversation → 대화 해설, key-expression → 핵심표현 해설)
//  (2) study/conversation-study: answerExplanation + sections 등(IProps)을 직접 받아 cloze 본문만 렌더
interface IExplanationProps extends Partial<IProps> {
  data?: TNotificationToast | null;
}

const Explanation: React.FC<IExplanationProps> = ({ data, ...clozeProps }) => {
  return (
    <>
      {/* (2) data가 없으면 cloze 본문 직접 렌더 (study 화면 경로) */}
      {data === undefined && <ClozeExplanation {...clozeProps} answerExplanation={clozeProps.answerExplanation} />}

      {/* (1) data가 있으면 toast type 기반 분기 */}
      {data && isClozeExplanationToastDataType(data) && (
        <ClozeExplanation
          answerExplanation={data.data.answerExplanation}
          addExplanation={data.data.addExplanation}
          clozeType={data.data.clozeType}
          labelSize='large'
        />
      )}
      {data && isVocabularyExplanationToastDataType(data) && (
        <AiVocabularyExplanation aiVocabulary={data.data.aiVocabulary} />
      )}
      {data && isConversationProblemToastDataType(data) && <ConversationExplanation data={data.data} />}
      {data && isKeyExpressionExplanationToastDataType(data) && <KeyExpressionExplanation data={data.data} />}
    </>
  );
};

export default Explanation;
